import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { PageTitleService } from '../../core/services/page-title.service';
import { PasswordResetService } from '../../core/services/password-reset.service';
import { PagerComponent } from '../../shared/components/pager.component';

@Component({
    selector: 'app-password-reset-list',
    imports: [RouterLink, FormsModule, DatePipe, PagerComponent],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="card">
    <div class="card-header">
      <span><i class="fa s-icon-list"></i> 一覧 <span>(全 {{items.length}} 件)</span></span>
    </div>
    <div class="card-body">
      <div class="row">
        <div class="col-md-12"><div style="height: 1px"></div></div>
      </div>
    </div>
    <div class="card-body">
      <div class="form-group row">
        <div class="col-md-12">
          <a class="btn btn-primary text-white" routerLink="/password-reset/execute" [class.disabled]="disableExecution">
            <i class="fa s-icon-passward"></i>&nbsp;パスワード付与
          </a>
          <button class="btn btn-success text-white" type="button" (click)="loadList()"><i class="fa fa-refresh"></i>&nbsp;画面更新</button>
        </div>
      </div>
          <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
          @if (items.length === 0) {
            <span>該当するデータはありません</span>
          }
            <table class="table table-bordered table-sm table-hover table-ptb-0">
              @if (items.length > 0) {
              <thead>
                <tr>
                  <th width="10%">状態</th>
                  <th width="25%">店舗名</th>
                  <th width="25%">処理日時</th>
                  <th width="10%"></th>
                </tr>
              </thead>
              <tbody>
                @for (item of pagedItems; track item.id) {
                  <tr>
                    <td class="text-center">
                      @if (+item.status === 0) {
                        <button class="btn text-secondary p-0 bg-inherit active" type="button" title="処理前"><i class="fa fa-2x fa-circle-o"></i></button>
                      }
                      @if (+item.status === 1) {
                        <button class="btn text-warning bg-inherit p-0 active" type="button" title="処理中"><i class="fa fa-2x fa-play-circle-o"></i></button>
                      }
                      @if (+item.status === 2) {
                        <button class="btn text-success bg-inherit p-0 active" type="button" title="成功"><i class="fa fa-2x fa-check-circle-o"></i></button>
                      }
                      @if (+item.status === 3) {
                        <button class="btn text-danger bg-inherit p-0 active" type="button" title="失敗"><i class="fa fa-2x fa-times-circle-o"></i></button>
                      }
                    </td>
                    <td>{{item.studio?.name}}</td>
                    <td>{{item.updatedAt | date:'yyyy/MM/dd HH:mm'}}</td>
                    <td class="text-center">
                      @if (item.uploadedFile || item.fileName) {
                        <button type="button" class="mt-1 mb-1 btn btn-success btn-sm" (click)="openDownloadModal(item.uploadedFile || item.fileName)" title="ダウンロード">
                          <i class="fa s-icon-dawnload fa-fw"></i>
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
              }
            </table>
            <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
    </div>
  </div>
</div>

@if (showDownloadModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" id="downloadPasswordModal" tabindex="-1" role="dialog" aria-labelledby="downloadPasswordModalLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="downloadPasswordModalLabel">会員付与パスワードダウンロード</h5>
          <button type="button" class="close" (click)="showDownloadModal = false">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <form novalidate name="downloadPasswordModalForm">
        <div class="modal-body">
          付与した会員パスワードをダウンロードしますか？
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showDownloadModal = false"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</button>
          <button class="btn btn-success" type="button" (click)="download()" [disabled]="disableDownload"><i class="fa s-icon-dawnload"></i>&nbsp;はい</button>
        </div>
        </form>
      </div>
    </div>
  </div>
}
  `
})
export class PasswordResetListComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private passwordResetService = inject(PasswordResetService);

  items: any[] = [];
  readonly pageLimit = 100;
  pageIndex = 0;

  get totalPages(): number { return Math.ceil(this.items.length / this.pageLimit); }
  get pagedItems(): any[] {
    return this.items.slice(this.pageIndex * this.pageLimit, (this.pageIndex + 1) * this.pageLimit);
  }
  goToPage(page: number): void {
    this.pageIndex = Math.max(0, Math.min(Math.trunc(page), this.totalPages - 1));
  }
  disableExecution = true;
  disableDownload = false;
  showDownloadModal = false;
  selectFile = '';

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('パスワード付与', 's-icon-passward');
    await this.loadList();
  }

  async loadList(): Promise<void> {
    const data: any[] = await this.passwordResetService.list() ?? [];
    this.disableExecution = data.some((v: any) => +v.status !== 2 && +v.status !== 3);
    this.items = data;
    this.goToPage(this.pageIndex);
  }

  openDownloadModal(uploadedFile: string): void {
    this.selectFile = uploadedFile;
    this.showDownloadModal = true;
  }

  async download(): Promise<void> {
    this.disableDownload = true;
    try {
      const data: any = await this.passwordResetService.getFile(this.selectFile);
      const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([bom, data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.selectFile;
      a.click();
      this.showDownloadModal = false;
    } finally {
      this.disableDownload = false;
    }
  }
}
